import settings from "src/services/settings.js";
/**
 * @typedef {object} CheckAvailabilityBody
 * @property {number} partySize
 * @property {string} rid
 * @property {string} startDateTime
 * @param {CheckAvailabilityBody} payload
 * @returns {Promise<import("src/modules/reservations/opentable.js").OTListingResult>}
 */
export async function fetchOTTimes(payload) {
  try {
    const body = JSON.stringify(payload);
    const res = await fetch(settings.otQueryTimesUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    if (!res.ok) {
      throw res.statusText;
    }
    /** @type {import("src/modules/reservations/opentable.js").OTV2Response} */
    const json = await res.json();

    // Handle OpenTable v2 API response structure
    let availabilityData = [];
    
    if (json.times_available && Array.isArray(json.times_available)) {
      availabilityData = json.times_available.flatMap(timeSlot => {
        return timeSlot.availability_types.flatMap(availabilityType => {

          return availabilityType.attributes.map(attribute => {
            const diningArea = availabilityType.dining_area?.find(area => 
              area.attributes?.includes(attribute) && area.experience_ids?.length > 0
            ) || availabilityType.dining_area?.[0];
            
            return {
              time: timeSlot.time,
              booking_url: diningArea?.booking_url || "",
              attribute: attribute || availabilityType.type.toLowerCase() || 'default',
              availabilityType: [availabilityType.type],
              experiences: (diningArea?.experience_ids || []).map(id => id.toString())
            };
          });
        });
      });
    }

    const result = {
      rid: Number(payload.rid),
      party_size: payload.partySize,
      times: availabilityData.map(item => item.time),
      times_available: availabilityData,
      href: json.href || "",
      no_availability_reasons: json.no_availability_reasons || [],
      errors: json.errors,
      requestid: json.requestid
    };

    if (json.no_availability_reasons?.length > 0) {
      throw json.no_availability_reasons[0];
    }

    return result;
  } catch (err) {
    throw new Error(err);
  }
}

/**
 * @param {import("src/modules/reservations/opentable.js").OTLockTime} payload
 * @returns {Promise<import("src/modules/reservations/opentable.js").OTPendingReservation>}
 */
export async function lockReservation(payload) {
  try {
    const body = JSON.stringify(payload);
    const res = await fetch(settings.otLockReservationUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    if (!res.ok) {
      throw res.statusText;
    }
    const json = await res.json();

    if (json.errors) {
      throw "unable to lock";
    }
    return json;
  } catch (err) {
    throw new Error(err);
  }
}

/**
 * @param {import("src/modules/reservations/opentable.js").OTConfirmPayload} payload
 * @returns {Promise<import("src/modules/reservations/opentable.js").OTConfirmation>}
 */
export async function createReservation(payload) {
  try {
    const body = JSON.stringify(payload);
    const res = await fetch(settings.otCreateReservationUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });
    const json = await res.json();
    if (!res.ok) {
      throw res.statusText;
    }
    return json;
  } catch (err) {
    throw new Error(err);
  }
}

/**
 * GET ALL EXPERIENCES
 * */
export async function fetchOTExperiences(rid) {
  const res = await fetch(settings.otExperiencesUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rid }),
  });

  const json = await res.json();
  let experiencesData = [];
  
  if (json.data && Array.isArray(json.data)) {
    experiencesData = json.data;
  } else if (json.experiences && Array.isArray(json.experiences)) {
    experiencesData = json.experiences;
  } else if (Array.isArray(json)) {
    experiencesData = json;
  }

  return {
    data: experiencesData
  };
}
